import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Tab, Tabs } from 'react-toolbox';
import S from './styles.scss';

class InstallerPrioritizationSliders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
    };
  }

  componentDidMount() {
    this.initializeAllInstallerNames();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.installersBySurfaceType !== this.props.installersBySurfaceType ||
        prevProps.surfaceTypes !== this.props.surfaceTypes) {
      this.initializeAllInstallerNames();
    }
  }

  getInstallersForSurface = (surfaceType) => {
    const { installersBySurfaceType } = this.props;
    return installersBySurfaceType[surfaceType] || [];
  };

  getSortedInstallers = (surfaceType) => {
    const list = this.getInstallersForSurface(surfaceType);
    return [...list].sort(
      (a, b) => (a.phase_count || 0) - (b.phase_count || 0),
    );
  };

  getInstallerWeight = (surfaceType, installerId) => {
    const { weights } = this.props;
    const surfaceWeights = weights[surfaceType] || {};
    const weightData = surfaceWeights[installerId];
    if (weightData === null || weightData === undefined) return 0;
    if (typeof weightData === 'object' && weightData.weight !== undefined) {
      return parseInt(weightData.weight, 10) || 0;
    }
    return parseInt(weightData, 10) || 0;
  };

  getUsedPercentage = (surfaceType) => {
    const { weights } = this.props;
    const surfaceWeights = weights[surfaceType] || {};
    return Object.values(surfaceWeights).reduce((sum, val) => {
      if (val === null || val === undefined) return sum;
      if (typeof val === 'object' && val.weight !== undefined) {
        return sum + (parseInt(val.weight, 10) || 0);
      }
      return sum + (parseInt(val, 10) || 0);
    }, 0);
  };

  initializeAllInstallerNames = () => {
    const { weights, surfaceTypes, installersBySurfaceType, onChange, disabled } = this.props;
    if (disabled || !surfaceTypes.length) return;

    let hasChanges = false;
    const newWeights = { ...weights };

    surfaceTypes.forEach((surfaceType) => {
      const installers = installersBySurfaceType[surfaceType] || [];
      if (!installers.length) return;

      const currentSurfaceWeights = weights[surfaceType] || {};
      const needsInit = installers.some((installer) => {
        const data = currentSurfaceWeights[installer.id];
        return !data || typeof data !== 'object' || !data.name;
      });

      if (needsInit) {
        hasChanges = true;
        const newSurfaceWeights = {};
        installers.forEach((installer) => {
          const existingData = currentSurfaceWeights[installer.id];
          let weight = 0;
          if (existingData !== null && existingData !== undefined) {
            weight = typeof existingData === 'object' && existingData.weight !== undefined
              ? parseInt(existingData.weight, 10) || 0
              : parseInt(existingData, 10) || 0;
          }
          newSurfaceWeights[installer.id] = { weight, name: installer.name };
        });
        newWeights[surfaceType] = newSurfaceWeights;
      }
    });

    if (hasChanges) {
      onChange(newWeights);
    }
  };

  buildSurfaceWeights = (surfaceType, changedInstallerId, newWeight) => {
    const installers = this.getInstallersForSurface(surfaceType);
    const newSurfaceWeights = {};

    installers.forEach((installer) => {
      const currentWeight = this.getInstallerWeight(surfaceType, installer.id);
      const weight = installer.id === changedInstallerId ? newWeight : currentWeight;
      newSurfaceWeights[installer.id] = { weight, name: installer.name };
    });

    return newSurfaceWeights;
  };

  handleSliderChange = (surfaceType, installerId, e) => {
    const value = parseInt(e.target.value, 10);
    const { weights, onChange } = this.props;
    const newSurfaceWeights = this.buildSurfaceWeights(surfaceType, installerId, value);
    const newWeights = { ...weights, [surfaceType]: newSurfaceWeights };
    onChange(newWeights);
  };

  handleInputChange = (surfaceType, installerId, e) => {
    const { weights, onChange } = this.props;
    const inputValue = e.target.value;

    let numValue;
    if (inputValue === '' || inputValue === null) {
      numValue = 0;
    } else {
      numValue = parseInt(inputValue, 10);
      if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) return;
    }

    const newSurfaceWeights = this.buildSurfaceWeights(surfaceType, installerId, numValue);
    const newWeights = { ...weights, [surfaceType]: newSurfaceWeights };
    onChange(newWeights);
  };

  handleTabChange = (index) => {
    this.setState({ activeTab: index });
  };

  renderSurfaceTypeContent = (surfaceType) => {
    const { disabled } = this.props;
    const sortedInstallers = this.getSortedInstallers(surfaceType);
    const usedPercentage = this.getUsedPercentage(surfaceType);

    if (sortedInstallers.length === 0) {
      return (
        <div className={S.tabContent}>
          <div className={S.noInstallersMessage}>
            No installers with crews covering this surface type.
          </div>
        </div>
      );
    }

    return (
      <div className={S.tabContent}>
        <div className={S.slidersSection}>
          {sortedInstallers.map(installer => (
            <div key={installer.id} className={S.sliderRow}>
              <div className={S.installerInfo}>
                <span className={S.installerLabel}>{installer.name}</span>
                <span className={S.phaseCount}>
                  ({installer.phase_count} phases)
                </span>
              </div>
              <div className={S.sliderControls}>
                <div className={S.sliderWrapper}>
                  <input
                    type="range"
                    className={S.slider}
                    min={0}
                    max={100}
                    step={5}
                    value={this.getInstallerWeight(surfaceType, installer.id)}
                    onChange={e => this.handleSliderChange(surfaceType, installer.id, e)}
                    disabled={disabled}
                  />
                  <div className={S.sliderRuler}>
                    {[0, 25, 50, 75, 100].map(tick => (
                      <span key={tick} className={S.rulerTick}>{tick}</span>
                    ))}
                  </div>
                </div>
                <div className={S.inputGroup}>
                  <input
                    type="number"
                    className={S.percentInput}
                    min={0}
                    max={100}
                    value={this.getInstallerWeight(surfaceType, installer.id)}
                    onChange={e => this.handleInputChange(surfaceType, installer.id, e)}
                    disabled={disabled}
                  />
                  <span className={S.percentSymbol}>%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={S.totalRow}>
          <span className={S.totalLabel}>Total:</span>
          <span className={usedPercentage !== 100 ? S.totalValueInvalid : S.totalValue}>
            {usedPercentage}%
          </span>
        </div>
      </div>
    );
  };

  render() {
    const { surfaceTypes } = this.props;
    const { activeTab } = this.state;

    if (!surfaceTypes || surfaceTypes.length === 0) {
      return (
        <div className={S.container}>
          <div className={S.noInstallersMessage}>
            No IFI surface types configured.
          </div>
        </div>
      );
    }

    return (
      <div className={S.container}>
        <Tabs index={activeTab} onChange={this.handleTabChange} className={S.tabs}>
          {surfaceTypes.map(surfaceType => (
            <Tab key={surfaceType} label={surfaceType}>
              {this.renderSurfaceTypeContent(surfaceType)}
            </Tab>
          ))}
        </Tabs>
      </div>
    );
  }
}

InstallerPrioritizationSliders.propTypes = {
  weights: PropTypes.object,
  installersBySurfaceType: PropTypes.object,
  surfaceTypes: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

InstallerPrioritizationSliders.defaultProps = {
  weights: {},
  installersBySurfaceType: {},
  surfaceTypes: [],
  disabled: false,
};

export default InstallerPrioritizationSliders;



// WEBPACK FOOTER //
// ./client/app/shared/components/InstallerPrioritizationSliders/index.jsx